import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RefreshCcw, Search, ChevronDown, ChevronRight, Download, FileText } from "lucide-react";
import {
  fetchAssignmentSummary,
  selectAssignmentSummary,
  setSearch,
  toggleCollector,
  toggleRecord,
} from "../store/assignmentSummarySlice.js";

const AssignmentsSummaryTab = ({ onRecordSelect }) => {
  const dispatch = useDispatch();
  const { rows, loading, error, search, openCollectors, openRecords } = useSelector(
    selectAssignmentSummary
  );

  const fmtPT = (iso) => {
    if (!iso) return "—";
    try {
      return new Intl.DateTimeFormat("en-US", {
        timeZone: "America/Los_Angeles",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      }).format(new Date(iso));
    } catch {
      return String(iso);
    }
  };

  useEffect(() => {
    if (rows.length > 0) return;
    dispatch(fetchAssignmentSummary())
      .unwrap()
      .catch((e) => {
        console.error("Failed to fetch assignments summary:", e);
      });
  }, [dispatch, rows.length]);

  const safeCollectorKey = (c) => String(c?.id || c?._id || c?.username || "unassigned");

  // group by collector
  const grouped = useMemo(() => {
    const q = search.trim().toLowerCase();

    const normalized = (rows || []).map((r) => ({
      recordId: r?.recordId,
      provider: r?.provider || "",
      ptName: r?.ptName || "",
      adjNumber: Array.isArray(r?.adjNumber) ? r.adjNumber : [],
      assignedAt: r?.assignedAt,
      assignedCollector: r?.assignedCollector || null,
      assignedBy: r?.assignedBy || null,
      assignmentHistory: Array.isArray(r?.assignmentHistory) ? r.assignmentHistory : [],
    }));

    const filtered = !q
      ? normalized
      : normalized.filter((r) => {
          const provider = String(r.provider || "").toLowerCase();
          const pt = String(r.ptName || "").toLowerCase();
          const adj = (r.adjNumber || []).join(",").toLowerCase();

          const cu = String(r?.assignedCollector?.username || "").toLowerCase();
          const cf = String(r?.assignedCollector?.fullName || "").toLowerCase();

          return provider.includes(q) || pt.includes(q) || adj.includes(q) || cu.includes(q) || cf.includes(q);
        });

    const map = new Map();

    filtered.forEach((r) => {
      const c = r.assignedCollector;
      const key = safeCollectorKey(c);

      if (!map.has(key)) {
        map.set(key, {
          collectorKey: key,
          collector: c
            ? { id: c.id || c._id || key, username: c.username || "", fullName: c.fullName || "" }
            : { id: "unassigned", username: "unassigned", fullName: "Unassigned" },
          records: [],
        });
      }

      map.get(key).records.push(r);
    });

    const groups = Array.from(map.values()).sort((a, b) => {
      const aUn = a.collector?.id === "unassigned" ? 1 : 0;
      const bUn = b.collector?.id === "unassigned" ? 1 : 0;
      if (aUn !== bUn) return aUn - bUn;

      const an = (a.collector?.fullName || a.collector?.username || "").toLowerCase();
      const bn = (b.collector?.fullName || b.collector?.username || "").toLowerCase();
      return an.localeCompare(bn);
    });

    groups.forEach((g) => {
      g.records.sort((x, y) => new Date(y.assignedAt || 0).getTime() - new Date(x.assignedAt || 0).getTime());
    });

    return groups;
  }, [rows, search]);

  const openCollectorSet = useMemo(() => new Set(openCollectors), [openCollectors]);
  const openRecordSet = useMemo(() => new Set(openRecords), [openRecords]);

  // download csv helpers
  const csvEscape = (v) => {
    const s = v === null || v === undefined ? "" : String(v);
    const mustWrap = /[,"\n]/.test(s);
    const escaped = s.replace(/"/g, '""');
    return mustWrap ? `"${escaped}"` : escaped;
  };

  const downloadTextFile = (filename, text) => {
    const blob = new Blob([text], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const buildCsvAll = () => {
    const header = [
      "collectorId",
      "collectorUsername",
      "collectorFullName",
      "provider",
      "ptName",
      "adjNumber",
      "assignedAtPT",
      "historyCount",
      "historyJson",
      "recordId(hidden)",
    ];
    const lines = [header.join(",")];

    grouped.forEach((g) => {
      const c = g.collector || {};
      g.records.forEach((r) => {
        const history = Array.isArray(r.assignmentHistory) ? r.assignmentHistory : [];
        lines.push(
          [
            csvEscape(c.id || ""),
            csvEscape(c.username || ""),
            csvEscape(c.fullName || ""),
            csvEscape(r.provider || ""),
            csvEscape(r.ptName || ""),
            csvEscape((r.adjNumber || []).join(" | ")),
            csvEscape(fmtPT(r.assignedAt)),
            csvEscape(history.length),
            csvEscape(JSON.stringify(history)),
            csvEscape(r.recordId || ""),
          ].join(",")
        );
      });
    });

    return lines.join("\n");
  };

  const buildCsvCollector = (group) => {
    const header = ["provider", "ptName", "adjNumber", "assignedAtPT", "historyCount", "historyJson", "recordId(hidden)"];
    const lines = [header.join(",")];

    (group?.records || []).forEach((r) => {
      const history = Array.isArray(r.assignmentHistory) ? r.assignmentHistory : [];
      lines.push(
        [
          csvEscape(r.provider || ""),
          csvEscape(r.ptName || ""),
          csvEscape((r.adjNumber || []).join(" | ")),
          csvEscape(fmtPT(r.assignedAt)),
          csvEscape(history.length),
          csvEscape(JSON.stringify(history)),
          csvEscape(r.recordId || ""),
        ].join(",")
      );
    });

    return lines.join("\n");
  };

  const handleDownloadAll = () => {
    const csv = buildCsvAll();
    downloadTextFile(`assignments_all_collectors_${new Date().toISOString().slice(0, 10)}.csv`, csv);
  };

  const handleDownloadCollector = (group) => {
    const c = group?.collector || {};
    const name = (c.username || c.fullName || c.id || "collector").replace(/\s+/g, "_");
    const csv = buildCsvCollector(group);
    downloadTextFile(`assignments_${name}_${new Date().toISOString().slice(0, 10)}.csv`, csv);
  };

  const handleOpenDetails = async (recordId) => {
    if (!recordId) return;
    // ✅ this will hit GET /api/records/:id via AdminPage handler, then open RecordDetails.jsx (old behavior)
    if (typeof onRecordSelect === "function") {
      await onRecordSelect(recordId);
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Assignments Summary</h2>
          <p className="text-sm text-gray-600">
            Expand a collector to see <b>Provider / Patient / Adj#</b> and assignment history (PT time).
          </p>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              value={search}
              onChange={(e) => dispatch(setSearch(e.target.value))}
              placeholder="Search provider / ptName / adj# / collector..."
              className="w-full sm:w-80 rounded-lg border border-gray-200 bg-white pl-9 pr-3 py-2 text-sm outline-none focus:border-blue-400"
            />
          </div>

          <button
            onClick={handleDownloadAll}
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            disabled={loading || grouped.length === 0}
          >
            <Download className="h-4 w-4" />
            Download All (CSV)
          </button>

          <button
            onClick={() => dispatch(fetchAssignmentSummary())}
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            disabled={loading}
          >
            <RefreshCcw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </div>

      {error && <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-red-800">{error}</div>}

      {/* Collector accordion */}
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
        <div className="divide-y divide-gray-100">
          {loading ? (
            <div className="px-4 py-6 text-gray-500">Loading...</div>
          ) : grouped.length === 0 ? (
            <div className="px-4 py-6 text-gray-500">No assignments found.</div>
          ) : (
            grouped.map((g) => {
              const c = g.collector || {};
              const isOpen = openCollectorSet.has(g.collectorKey);
              const total = g.records.length;
              const lastAssigned = g.records[0]?.assignedAt ? fmtPT(g.records[0].assignedAt) : "—";

              return (
                <div key={g.collectorKey} className="px-4 py-3">
                  {/* Collector Row */}
                  <div className="flex items-center justify-between gap-3">
                    <button
                      onClick={() => dispatch(toggleCollector(g.collectorKey))}
                      className="flex items-center gap-2 text-left min-w-0"
                    >
                      {isOpen ? (
                        <ChevronDown className="h-4 w-4 text-gray-600" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-gray-600" />
                      )}
                      <div className="min-w-0">
                        <div className="font-semibold text-gray-900 truncate">
                          {c.fullName || c.username || "Unassigned"}
                        </div>
                        <div className="text-xs text-gray-500 truncate">{c.username ? `@${c.username}` : ""}</div>
                      </div>
                    </button>

                    <div className="flex items-center gap-2 flex-shrink-0">
                      <div className="hidden sm:flex items-center gap-3 text-xs text-gray-600">
                        <span className="rounded-full border border-gray-200 bg-gray-50 px-2 py-1">
                          Records: <b className="text-gray-900">{total}</b>
                        </span>
                        <span className="rounded-full border border-gray-200 bg-gray-50 px-2 py-1">
                          Last: <b className="text-gray-900">{lastAssigned}</b>
                        </span>
                      </div>

                      <button
                        onClick={() => handleDownloadCollector(g)}
                        className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50"
                      >
                        <FileText className="h-4 w-4" />
                        CSV
                      </button>
                    </div>
                  </div>

                  {/* Expanded Records */}
                  {isOpen && (
                    <div className="mt-3 overflow-hidden rounded-lg border border-gray-200">
                      <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                          <thead className="bg-gray-50 text-gray-600">
                            <tr>
                              <th className="px-4 py-3 text-left font-semibold">Provider</th>
                              <th className="px-4 py-3 text-left font-semibold">Patient</th>
                              <th className="px-4 py-3 text-left font-semibold">Adj #</th>
                              <th className="px-4 py-3 text-left font-semibold">Assigned (PT)</th>
                              <th className="px-4 py-3 text-left font-semibold">History</th>
                              <th className="px-4 py-3 text-left font-semibold">Actions</th>
                            </tr>
                          </thead>

                          <tbody className="divide-y divide-gray-100">
                            {g.records.map((r) => {
                              const rid = String(r.recordId || "");
                              const history = Array.isArray(r.assignmentHistory) ? r.assignmentHistory : [];
                              const recOpen = openRecordSet.has(rid);

                              return (
                                <React.Fragment key={rid}>
                                  <tr className="hover:bg-gray-50">
                                    <td className="px-4 py-3 text-gray-800">{r.provider || "—"}</td>
                                    <td className="px-4 py-3 text-gray-800">{r.ptName || "—"}</td>
                                    <td className="px-4 py-3 text-gray-800">{(r.adjNumber || []).join(", ") || "—"}</td>
                                    <td className="px-4 py-3 text-gray-800">{fmtPT(r.assignedAt)}</td>

                                    <td className="px-4 py-3 text-gray-800">
                                      <button
                                        onClick={() => dispatch(toggleRecord(rid))}
                                        className="inline-flex items-center gap-2 rounded-md border border-gray-200 bg-white px-2 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-50"
                                        disabled={history.length === 0}
                                        title="Show assignment history"
                                      >
                                        {recOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                        {history.length} item{history.length !== 1 ? "s" : ""}
                                      </button>
                                    </td>

                                    <td className="px-4 py-3 text-gray-800">
                                      <button
                                        onClick={() => handleOpenDetails(rid)}
                                        className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700"
                                      >
                                        Open Details
                                      </button>
                                    </td>
                                  </tr>

                                  {/* history expanded */}
                                  {recOpen && history.length > 0 && (
                                    <tr className="bg-gray-50">
                                      <td className="px-4 py-3" colSpan={6}>
                                        <div className="rounded-lg border border-gray-200 bg-white p-3">
                                          <div className="text-xs font-semibold text-gray-700 mb-2">
                                            Assignment History
                                          </div>

                                          <div className="overflow-x-auto">
                                            <table className="min-w-full text-xs">
                                              <thead className="text-gray-600">
                                                <tr>
                                                  <th className="px-2 py-2 text-left font-semibold">From</th>
                                                  <th className="px-2 py-2 text-left font-semibold">To</th>
                                                  <th className="px-2 py-2 text-left font-semibold">Assigned By</th>
                                                  <th className="px-2 py-2 text-left font-semibold">Assigned At (PT)</th>
                                                </tr>
                                              </thead>
                                              <tbody className="divide-y divide-gray-100">
                                                {history.map((h, idx) => (
                                                  <tr key={idx} className="hover:bg-gray-50">
                                                    <td className="px-2 py-2 text-gray-800">
                                                      {h?.fromCollector?.fullName || h?.fromCollector?.username || "—"}
                                                    </td>
                                                    <td className="px-2 py-2 text-gray-800">
                                                      {h?.toCollector?.fullName || h?.toCollector?.username || "—"}
                                                    </td>
                                                    <td className="px-2 py-2 text-gray-800">
                                                      {h?.assignedBy?.fullName || h?.assignedBy?.username || "—"}
                                                    </td>
                                                    <td className="px-2 py-2 text-gray-800">{fmtPT(h?.assignedAt)}</td>
                                                  </tr>
                                                ))}
                                              </tbody>
                                            </table>
                                          </div>
                                        </div>
                                      </td>
                                    </tr>
                                  )}
                                </React.Fragment>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      <div className="border-t border-gray-100 px-4 py-3 text-xs text-gray-500">
                        Showing {g.records.length} record(s) for this collector
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        <div className="border-t border-gray-100 px-4 py-3 text-xs text-gray-500">
          Collectors: {grouped.length} | Records: {rows.length}
        </div>
      </div>
    </div>
  );
};

export default AssignmentsSummaryTab;
